import React, { useState } from "react"

import { useQuery } from "@tanstack/react-query"
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Copy,
  Download,
  Info,
  Loader2,
  Search,
} from "lucide-react"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import DashboardLayout from "@/layouts/DashboardLayout"
import { supabase } from "@/lib/supabase"

type SearchMode = "name" | "date"

interface Endorsement {
  penaltyPoints?: number
  offenceCode?: string
  convictionDate?: string
  expiryDate?: string
}

interface DVLAConsentRecord {
  id: string
  driving_licence_number: string | null
  first_name: string | null
  last_name: string | null
  gender: string | null
  date_of_birth: string | null
  address_line1: string | null
  address_line2: string | null
  address_line5: string | null
  postcode: string | null
  licence_type: string | null
  licence_status: string | null
  token_issue_number: number | null
  token_valid_from: string | null
  token_valid_to: string | null
  endorsements: Endorsement[] | null
  entitlement: Record<string, unknown>[] | null
  cpc: Record<string, unknown> | null
  holder: Record<string, unknown> | null
  eligible: boolean | null
  created_at: string
  updated_at: string | null
  application: {
    dvla_consent_accepted_at: string | null
    application_id: string
  } | null
}

const PAGE_SIZE = 25

/** Simple key-value detail row for the expanded panel */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-2 py-1.5 border-b border-palette-jungleGreen100 last:border-0">
      <span className="text-xs font-medium text-palette-jungleGreen500 w-[140px] shrink-0">
        {label}
      </span>
      <span className="text-xs text-palette-jungleGreen900">{value || "—"}</span>
    </div>
  )
}

function getPenaltyPoints(endorsements: Endorsement[] | null): number {
  if (!endorsements || !Array.isArray(endorsements)) return 0
  return endorsements.reduce((total, e) => total + (Number(e.penaltyPoints) || 0), 0)
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—"
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  } catch {
    return "—"
  }
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return "—"
  try {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return "—"
  }
}

function formatAddress(record: DVLAConsentRecord): string {
  return [record.address_line1, record.address_line2, record.address_line5]
    .filter(Boolean)
    .join(", ")
}

function exportToCsv(records: DVLAConsentRecord[]) {
  const headers = [
    "Name",
    "Date of Birth",
    "Address",
    "Postcode",
    "Licence Number",
    "Consent Accepted",
    "DVLA Check Date",
    "Licence Status",
    "Eligible",
    "Penalty Points",
    "Endorsements",
  ]

  const rows = records.map((r) => [
    `${r.first_name ?? ""} ${r.last_name ?? ""}`.trim(),
    formatDate(r.date_of_birth),
    formatAddress(r),
    r.postcode ?? "",
    r.driving_licence_number ?? "",
    formatDateTime(r.application?.dvla_consent_accepted_at ?? null),
    formatDateTime(r.created_at),
    r.licence_status ?? "",
    r.eligible === null ? "" : r.eligible ? "Yes" : "No",
    String(getPenaltyPoints(r.endorsements)),
    String(Array.isArray(r.endorsements) ? r.endorsements.length : 0),
  ])

  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(","))
    .join("\n")

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `dvla-consent-audit-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

const DVLAConsentAudit: React.FC = () => {
  const [searchMode, setSearchMode] = useState<SearchMode>("name")
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [activeDateFrom, setActiveDateFrom] = useState("")
  const [activeDateTo, setActiveDateTo] = useState("")
  const [page, setPage] = useState(0)
  const [hasSearched, setHasSearched] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSearch = () => {
    if (searchMode === "name") {
      setSearch(searchInput.trim())
      setActiveDateFrom("")
      setActiveDateTo("")
    } else {
      setSearch("")
      setActiveDateFrom(dateFrom)
      setActiveDateTo(dateTo)
    }
    setPage(0)
    setHasSearched(true)
  }

  const isSearchDisabled = searchMode === "name" ? !searchInput.trim() : !dateFrom && !dateTo

  const queryKey =
    searchMode === "name"
      ? ["dvla-consent-audit", "name", search, page]
      : ["dvla-consent-audit", "date", activeDateFrom, activeDateTo, page]

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from("driver_licence_records")
        .select(
          `
          id, driving_licence_number, first_name, last_name, gender,
          date_of_birth, address_line1, address_line2, address_line5, postcode,
          licence_type, licence_status, token_issue_number, token_valid_from, token_valid_to,
          endorsements, entitlement, cpc, holder,
          eligible, created_at, updated_at,
          application:application_id(dvla_consent_accepted_at, application_id)
        `,
          { count: "exact" },
        )
        .order("created_at", { ascending: false })

      if (search) {
        query = query.or(
          `first_name.ilike.%${search}%,last_name.ilike.%${search}%,driving_licence_number.ilike.%${search}%`,
        )
      }

      if (activeDateFrom) {
        query = query.gte("created_at", `${activeDateFrom}T00:00:00.000Z`)
      }
      if (activeDateTo) {
        query = query.lte("created_at", `${activeDateTo}T23:59:59.999Z`)
      }

      const from = page * PAGE_SIZE
      query = query.range(from, from + PAGE_SIZE - 1)

      const { data, error, count } = await query
      if (error) throw error
      return {
        records: (data ?? []) as unknown as DVLAConsentRecord[],
        count: count ?? 0,
      }
    },
    enabled: hasSearched && (search.length > 0 || !!activeDateFrom || !!activeDateTo),
  })

  const records = data?.records ?? []
  const totalCount = data?.count ?? 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>DVLA Consent Audit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-200 bg-blue-50">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800 text-xs leading-relaxed">
                HIVED must be able to evidence that the Data Protection Declaration was
                electronically presented to and read by the driver, along with the date this
                occurred. Each consent record below captures the driver's acceptance timestamp and
                DVLA check details. Consent expires when the driver ceases driving in connection
                with HIVED or after 3 years from the date of submission, whichever is sooner.
              </AlertDescription>
            </Alert>

            {/* Search mode tabs */}
            <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1 w-fit">
              <button
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  searchMode === "name"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setSearchMode("name")}
              >
                <Search className="inline mr-1.5 h-3.5 w-3.5" />
                Name / Licence
              </button>
              <button
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  searchMode === "date"
                    ? "bg-background shadow-sm text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => setSearchMode("date")}
              >
                <Calendar className="inline mr-1.5 h-3.5 w-3.5" />
                Date Range
              </button>
            </div>

            {searchMode === "name" ? (
              <div className="flex gap-2">
                <Input
                  placeholder="Search by name or licence number..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="max-w-md"
                />
                <Button onClick={handleSearch} disabled={isSearchDisabled}>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </div>
            ) : (
              <div className="flex items-end gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="date-from" className="text-xs">
                    From
                  </Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-[160px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="date-to" className="text-xs">
                    To
                  </Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-[160px]"
                  />
                </div>
                <Button onClick={handleSearch} disabled={isSearchDisabled}>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {hasSearched && (search || activeDateFrom || activeDateTo) && (
          <Card>
            <CardContent className="pt-6">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : records.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No records found{search ? ` for "${search}"` : " for the selected date range"}.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-muted-foreground">
                      {totalCount} record{totalCount !== 1 ? "s" : ""} found
                    </p>
                    <Button variant="outline" size="sm" onClick={() => exportToCsv(records)}>
                      <Download className="mr-2 h-4 w-4" />
                      Export CSV
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>DOB</TableHead>
                          <TableHead>Address</TableHead>
                          <TableHead>Postcode</TableHead>
                          <TableHead>Licence No.</TableHead>
                          <TableHead>Consent Accepted</TableHead>
                          <TableHead>DVLA Check Date</TableHead>
                          <TableHead>Licence Status</TableHead>
                          <TableHead>Eligible</TableHead>
                          <TableHead>Points</TableHead>
                          <TableHead>Endorsements</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {records.map((record) => {
                          const points = getPenaltyPoints(record.endorsements)
                          const endorsementCount = Array.isArray(record.endorsements)
                            ? record.endorsements.length
                            : 0
                          const isExpanded = expandedRows.has(record.id)
                          return (
                            <React.Fragment key={record.id}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => toggleRow(record.id)}
                              >
                                <TableCell className="px-2">
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                  )}
                                </TableCell>
                                <TableCell className="font-medium whitespace-nowrap">
                                  {record.first_name} {record.last_name}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {formatDate(record.date_of_birth)}
                                </TableCell>
                                <TableCell className="max-w-[200px] truncate">
                                  {formatAddress(record)}
                                </TableCell>
                                <TableCell>{record.postcode ?? "—"}</TableCell>
                                <TableCell className="font-mono text-xs">
                                  {record.driving_licence_number ?? "—"}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {formatDateTime(
                                    record.application?.dvla_consent_accepted_at ?? null,
                                  )}
                                </TableCell>
                                <TableCell className="whitespace-nowrap">
                                  {formatDateTime(record.created_at)}
                                </TableCell>
                                <TableCell>{record.licence_status ?? "—"}</TableCell>
                                <TableCell>
                                  {record.eligible === null ? (
                                    "—"
                                  ) : record.eligible ? (
                                    <Badge variant="default">Eligible</Badge>
                                  ) : (
                                    <Badge variant="destructive">Ineligible</Badge>
                                  )}
                                </TableCell>
                                <TableCell>{points}</TableCell>
                                <TableCell>{endorsementCount}</TableCell>
                              </TableRow>
                              {isExpanded && (
                                <TableRow>
                                  <TableCell colSpan={12} className="bg-palette-jungleGreen000 p-0">
                                    <div className="p-4">
                                      <div className="flex items-center justify-between mb-3">
                                        <p className="text-xs font-semibold text-palette-jungleGreen600 uppercase tracking-wide">
                                          DVLA Record Details
                                        </p>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-7 text-xs"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            const { application: _app, ...dbRecord } = record
                                            navigator.clipboard.writeText(
                                              JSON.stringify(dbRecord, null, 2),
                                            )
                                          }}
                                        >
                                          <Copy className="mr-1.5 h-3 w-3" />
                                          Copy
                                        </Button>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Driver Details */}
                                        <div className="bg-white rounded-lg border border-palette-jungleGreen200 p-3">
                                          <p className="text-[10px] font-semibold text-palette-jungleGreen400 uppercase tracking-wider mb-2">
                                            Driver
                                          </p>
                                          <DetailRow label="First Name" value={record.first_name} />
                                          <DetailRow label="Last Name" value={record.last_name} />
                                          <DetailRow label="Gender" value={record.gender} />
                                          <DetailRow
                                            label="Date of Birth"
                                            value={formatDate(record.date_of_birth)}
                                          />
                                          <DetailRow
                                            label="Address"
                                            value={formatAddress(record)}
                                          />
                                          <DetailRow label="Postcode" value={record.postcode} />
                                        </div>

                                        {/* Licence Details */}
                                        <div className="bg-white rounded-lg border border-palette-jungleGreen200 p-3">
                                          <p className="text-[10px] font-semibold text-palette-jungleGreen400 uppercase tracking-wider mb-2">
                                            Licence
                                          </p>
                                          <DetailRow
                                            label="Licence Number"
                                            value={
                                              <span className="font-mono">
                                                {record.driving_licence_number}
                                              </span>
                                            }
                                          />
                                          <DetailRow
                                            label="Licence Type"
                                            value={record.licence_type}
                                          />
                                          <DetailRow
                                            label="Licence Status"
                                            value={record.licence_status}
                                          />
                                          <DetailRow
                                            label="Eligible"
                                            value={
                                              record.eligible === null
                                                ? "—"
                                                : record.eligible
                                                  ? "Yes"
                                                  : "No"
                                            }
                                          />
                                          <DetailRow
                                            label="Token Issue No."
                                            value={record.token_issue_number?.toString()}
                                          />
                                          <DetailRow
                                            label="Token Valid From"
                                            value={formatDate(record.token_valid_from)}
                                          />
                                          <DetailRow
                                            label="Token Valid To"
                                            value={formatDate(record.token_valid_to)}
                                          />
                                        </div>

                                        {/* Timestamps & Consent */}
                                        <div className="bg-white rounded-lg border border-palette-jungleGreen200 p-3">
                                          <p className="text-[10px] font-semibold text-palette-jungleGreen400 uppercase tracking-wider mb-2">
                                            Consent & Timestamps
                                          </p>
                                          <DetailRow
                                            label="Consent Accepted"
                                            value={formatDateTime(
                                              record.application?.dvla_consent_accepted_at ?? null,
                                            )}
                                          />
                                          <DetailRow
                                            label="DVLA Check Date"
                                            value={formatDateTime(record.created_at)}
                                          />
                                          <DetailRow
                                            label="Last Updated"
                                            value={formatDateTime(record.updated_at ?? null)}
                                          />
                                          <DetailRow
                                            label="Application ID"
                                            value={
                                              <span className="font-mono">
                                                {record.application?.application_id}
                                              </span>
                                            }
                                          />
                                          <DetailRow
                                            label="Record ID"
                                            value={<span className="font-mono">{record.id}</span>}
                                          />
                                        </div>
                                      </div>

                                      {/* Endorsements */}
                                      {Array.isArray(record.endorsements) &&
                                        record.endorsements.length > 0 && (
                                          <div className="mt-4 bg-white rounded-lg border border-palette-jungleGreen200 p-3">
                                            <p className="text-[10px] font-semibold text-palette-jungleGreen400 uppercase tracking-wider mb-2">
                                              Endorsements ({record.endorsements.length})
                                            </p>
                                            <div className="overflow-x-auto">
                                              <table className="w-full text-xs">
                                                <thead>
                                                  <tr className="border-b border-palette-jungleGreen200">
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Offence Code
                                                    </th>
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Points
                                                    </th>
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Conviction Date
                                                    </th>
                                                    <th className="text-left py-1.5 font-medium text-palette-jungleGreen500">
                                                      Expiry Date
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {record.endorsements.map((e, i) => (
                                                    <tr
                                                      key={i}
                                                      className="border-b border-palette-jungleGreen000 last:border-0"
                                                    >
                                                      <td className="py-1.5 pr-4 font-mono">
                                                        {e.offenceCode ?? "—"}
                                                      </td>
                                                      <td className="py-1.5 pr-4">
                                                        {e.penaltyPoints ?? "—"}
                                                      </td>
                                                      <td className="py-1.5 pr-4">
                                                        {formatDate(e.convictionDate ?? null)}
                                                      </td>
                                                      <td className="py-1.5">
                                                        {formatDate(e.expiryDate ?? null)}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}

                                      {/* Entitlements */}
                                      {Array.isArray(record.entitlement) &&
                                        record.entitlement.length > 0 && (
                                          <div className="mt-4 bg-white rounded-lg border border-palette-jungleGreen200 p-3">
                                            <p className="text-[10px] font-semibold text-palette-jungleGreen400 uppercase tracking-wider mb-2">
                                              Entitlements ({record.entitlement.length})
                                            </p>
                                            <div className="overflow-x-auto">
                                              <table className="w-full text-xs">
                                                <thead>
                                                  <tr className="border-b border-palette-jungleGreen200">
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Category
                                                    </th>
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Type
                                                    </th>
                                                    <th className="text-left py-1.5 pr-4 font-medium text-palette-jungleGreen500">
                                                      Valid From
                                                    </th>
                                                    <th className="text-left py-1.5 font-medium text-palette-jungleGreen500">
                                                      Valid To
                                                    </th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {record.entitlement.map((ent, i) => (
                                                    <tr
                                                      key={i}
                                                      className="border-b border-palette-jungleGreen000 last:border-0"
                                                    >
                                                      <td className="py-1.5 pr-4 font-mono">
                                                        {String(ent.code ?? "—")}
                                                      </td>
                                                      <td className="py-1.5 pr-4">
                                                        {String(ent.type ?? "—")}
                                                      </td>
                                                      <td className="py-1.5 pr-4">
                                                        {formatDate(String(ent.validFrom ?? ""))}
                                                      </td>
                                                      <td className="py-1.5">
                                                        {formatDate(String(ent.validTo ?? ""))}
                                                      </td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Page {page + 1} of {totalPages}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => p - 1)}
                          disabled={page === 0}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => p + 1)}
                          disabled={page >= totalPages - 1}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}

export default DVLAConsentAudit
